import { applySnapshot, Instance, SnapshotIn, SnapshotOut, types } from 'mobx-state-tree';
import { enableStaticRendering } from 'mobx-react';
import React, { createContext, useContext } from 'react';

// eslint-disable-next-line react-hooks/rules-of-hooks
enableStaticRendering(typeof window === 'undefined');

export const Storage = types
    .model('Storage', {
        darkMode: false,
        selectedTags: types.array(types.string),
        activeStory: types.maybe(types.number),
        userLikes: types.array(types.number),
        viewedStories: types.array(types.number),
        soundEmitter: types.maybe(types.string),
    })
    .actions((self) => ({
        setCategory(category: string | null) {},
        setDarkMode(state: boolean) {
            self.darkMode = state;
        },
        addTag(tag: string) {
            self.selectedTags.push(tag);
        },
        removeTag(tag: string) {
            self.selectedTags.splice(self.selectedTags.indexOf(tag), 1);
        },
        setActiveStory(id: number | undefined) {
            self.activeStory = id;
        },
        setUserLikes(likesArray: number[]) {
            self.userLikes.clear();
            self.userLikes.push(...likesArray);
        },
        addViewedStory(id: number) {
            if (!self.viewedStories.includes(id)) {
                self.viewedStories.push(id);
            }
        },
        setSoundEmitter(source: string) {
            self.soundEmitter = source;
        },
    }))
    .views((self) => ({}));

export type IStorage = Instance<typeof Storage>;
export type IStorageSnapshotIn = SnapshotIn<typeof Storage>;
export type IStorageSnapshotOut = SnapshotOut<typeof Storage>;
export const StorageContext = createContext<IStorage | null>(null);
export const { Provider } = StorageContext;

let localStorage: IStorage;

export function initializeStorage(snapshot?: IStorageSnapshotIn): IStorage {
    const tempStorage = localStorage ?? Storage.create();

    if (!localStorage && snapshot) {
        applySnapshot(tempStorage, snapshot);
    }

    if (typeof window === 'undefined') {
        return tempStorage;
    }

    if (!localStorage) {
        localStorage = tempStorage;
    }

    return localStorage;
}

export const useStorage = (): IStorage => {
    const storage = useContext(StorageContext);
    if (!storage) {
        throw Error('storage must be initialized');
    }
    return storage;
};
